import React, { useState, useRef, useEffect } from 'react';

type EditTrigger = 'click' | 'hover' | 'focus';

type InlineEditProps<T> = {
  value: T;
  onChange: (value: T) => void;
  renderDisplay: (value: T) => React.ReactNode;
  renderEditor: (
    value: T,
    onChange: (value: T) => void,
    onBlur: () => void,
    onFocus: () => void
  ) => React.ReactNode;
  editOn?: EditTrigger;
  disabled?: boolean;
  className?: string;
};

export function InlineEdit<T>({
  value,
  onChange,
  renderDisplay,
  renderEditor,
  editOn = 'hover',
  disabled = false,
  className,
}: InlineEditProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleStartEdit = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleEndEdit = () => {
    setIsEditing(false);
    setIsHovering(false);
  };

  const handleChange = (newValue: T) => {
    onChange(newValue);
  };

  // Handle click outside to end editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isEditing) return;

      const target = event.target as Node;

      // Check if click is inside the container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // Check if click is inside a ReactDatePicker calendar (rendered in portal)
      const datePickerElement = (target as Element).closest?.('.react-datepicker');
      if (datePickerElement) {
        return;
      }

      // Click is truly outside, end editing
      handleEndEdit();
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing]);

  const shouldShowEditor = isEditing || (editOn === 'hover' && isHovering);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={className}
      onMouseEnter={() => {
        if (editOn === 'hover' && !disabled) {
          setIsHovering(true);
        }
      }}
      onMouseLeave={() => {
        if (editOn === 'hover') {
          setIsHovering(false);
        }
      }}
      onClick={() => {
        if (editOn === 'click' && !disabled) {
          handleStartEdit();
        }
      }}
      onFocus={() => {
        if (editOn === 'focus' && !disabled) {
          handleStartEdit();
        }
      }}
    >
      {shouldShowEditor ? (
        <div>{renderEditor(value, handleChange, handleEndEdit, handleStartEdit)}</div>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              handleStartEdit();
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              handleStartEdit();
            }
          }}
          disabled={disabled}
          className="tw:w-full tw:text-left tw:px-[calc(theme(spacing[2])-1px)] tw:py-[calc(theme(spacing[0.5])-1px)] tw:sm:px-[calc(theme(spacing[1.5])-1px)] tw:sm:py-[1px] tw:text-xs/4 tw:text-zinc-950 tw:sm:text-[10px]/4 tw:dark:text-white tw:cursor-text tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:rounded-lg tw:disabled:opacity-50 tw:disabled:cursor-not-allowed"
        >
          {renderDisplay(value)}
        </button>
      )}
    </div>
  );
}
